import React, { useEffect, useId, useRef, useState } from 'react';
import { MapPin, Loader } from 'lucide-react';
import { searchLocations } from '../services/api';
import './LocationInput.css';

// Ortseingabe mit Autocomplete gegen GET /api/locations/search.
// onChange(text, location) — location ist die gewählte Zeile
// ({ id, name, plz, lat, lng }) oder null, solange frei getippt wird.
// Der Aufrufer hält damit die location_id im State, nicht nur den Text.
export default function LocationInput({
  value = '',
  onChange,
  placeholder = 'Ort eingeben, z.B. Kaiserslautern',
  className = '',
  inputClassName = '',
  autoFocus = false,
  required = false,
  onKeyDown,
}) {
  const listId = useId();
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  // Nach einer Auswahl ändert sich value — das darf keine neue Suche auslösen
  const skipSearch = useRef(false);

  useEffect(() => {
    if (skipSearch.current) { skipSearch.current = false; return; }

    const q = value.trim();
    if (q.length < 2) { setResults([]); setOpen(false); setLoading(false); return; }

    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      searchLocations(q)
        .then(({ data }) => {
          if (cancelled) return;
          const list = Array.isArray(data) ? data : data?.locations || [];
          setResults(list);
          setOpen(list.length > 0);
          setActive(-1);
        })
        .catch(() => { if (!cancelled) { setResults([]); setOpen(false); } })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [value]);

  const select = (loc) => {
    skipSearch.current = true;
    setResults([]);
    setOpen(false);
    setActive(-1);
    onChange(loc.name, loc);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setActive(-1);
      return;
    }
    if (open && results.length) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive(i => (i + 1) % results.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive(i => (i <= 0 ? results.length : i) - 1);
        return;
      }
      if (e.key === 'Enter' && active >= 0) {
        e.preventDefault();
        select(results[active]);
        return;
      }
    }
    onKeyDown && onKeyDown(e);
  };

  return (
    <div className={`location-input ${className}`}>
      <input
        type="text"
        className={inputClassName || 'location-input__field'}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        autoFocus={autoFocus}
        required={required}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        onChange={e => onChange(e.target.value, null)}
        onKeyDown={handleKeyDown}
        onFocus={() => results.length > 0 && setOpen(true)}
        onBlur={() => setOpen(false)}
      />
      {loading && <Loader size={15} className="location-input__spinner spin" />}

      {open && (
        <ul id={listId} className="location-input__list" role="listbox">
          {results.map((loc, i) => (
            <li key={loc.id}>
              <button
                type="button"
                role="option"
                aria-selected={i === active}
                className={`location-input__item${i === active ? ' is-active' : ''}`}
                // mousedown statt click: sonst schließt das blur die Liste vorher
                onMouseDown={e => { e.preventDefault(); select(loc); }}
                onMouseEnter={() => setActive(i)}
              >
                <MapPin size={14} />
                <span className="location-input__name">{loc.name}</span>
                {loc.plz && <span className="location-input__plz">{loc.plz}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
